import { createAssetReference } from './asset_reference_contract.mjs';
import { assetVariantId, createAssetVariantParameters } from './asset_variant_identity.mjs';

const REASONS = new Set(['legacy-unpinned', 'missing-article', 'missing-asset', 'unversioned-asset']);
const check = (ok, message) => { if (!ok) throw Object.assign(new TypeError(`Asset bindings: ${message}`), { code: 'INVALID_ASSET_BINDINGS' }); };
function fields(value, keys) {
  check(value && typeof value === 'object' && !Array.isArray(value), 'object required');
  const descriptors = Object.getOwnPropertyDescriptors(value);
  check(Reflect.ownKeys(descriptors).length === keys.length && keys.every(key => descriptors[key]?.enumerable
    && Object.hasOwn(descriptors[key], 'value')), 'unexpected or accessor fields');
  return Object.fromEntries(keys.map(key => [key, descriptors[key].value]));
}
function text(value, label) {
  check(typeof value === 'string' && value.length > 0 && value.length <= 256 && value.trim() === value
    && value.isWellFormed() && [...value].every(char => char.charCodeAt(0) >= 32), `invalid ${label}`);
  return value;
}

const COLOR = /^#[0-9A-F]{6}$/;
const REGION = /^(0|[1-9][0-9]{0,4})$/;

const VIEWS = ['top', 'bottom', 'front', 'back', 'left', 'right'];
function regionColors(regions) {
  check(regions && typeof regions === 'object' && !Array.isArray(regions) && Object.getPrototypeOf(regions) === Object.prototype, 'region colours object required');
  const keys = Reflect.ownKeys(regions);
  check(keys.length <= 20000, 'too many region colours');
  const copy = {};
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(regions, key);
    check(typeof key === 'string' && REGION.test(key) && Number(key) < 20000 && descriptor.enumerable
      && Object.hasOwn(descriptor, 'value') && typeof descriptor.value === 'string' && COLOR.test(descriptor.value), 'invalid region colour');
    copy[key] = descriptor.value;
  }
  // Canonical numeric key order keeps equality checks and serialisation stable.
  return Object.freeze(Object.fromEntries(Object.keys(copy).sort((a, b) => Number(a) - Number(b)).map(key => [key, copy[key]])));
}

/**
 * Colour state of a pin's compatibility artefacts (legacy GLB/SVG) at pin time:
 * one GLB colour (null keeps the original materials), fill-region colours of the
 * legacy top view by region order and, optionally (binding contract version 3),
 * non-empty fill-region colours of individual legacy projection views.
 */
export function createLegacyAssetAppearance(value) {
  const withViews = Object.hasOwn(value || {}, 'legacySvgViewRegionColors');
  const appearance = fields(value, ['legacyGlbColor', 'legacySvgRegionColors', ...(withViews ? ['legacySvgViewRegionColors'] : [])]);
  check(appearance.legacyGlbColor === null || (typeof appearance.legacyGlbColor === 'string' && COLOR.test(appearance.legacyGlbColor)), 'invalid GLB colour');
  const result = { legacyGlbColor: appearance.legacyGlbColor, legacySvgRegionColors: regionColors(appearance.legacySvgRegionColors) };
  if (withViews) {
    const views = appearance.legacySvgViewRegionColors;
    check(views && typeof views === 'object' && !Array.isArray(views) && Object.getPrototypeOf(views) === Object.prototype, 'view colours object required');
    const keys = Reflect.ownKeys(views);
    check(keys.length > 0 && keys.every(key => VIEWS.includes(key) && Object.getOwnPropertyDescriptor(views, key).enumerable
      && Object.hasOwn(Object.getOwnPropertyDescriptor(views, key), 'value')), 'invalid view colours');
    const copy = VIEWS.filter(view => keys.includes(view)).map(view => [view, regionColors(views[view])]);
    check(copy.every(([, colors]) => Object.keys(colors).length > 0), 'empty view colours');
    result.legacySvgViewRegionColors = Object.freeze(Object.fromEntries(copy));
  }
  return Object.freeze(result);
}

/**
 * Owner-local component associations. Presence never proves complete project coverage.
 * Version 2 adds an optional colour state to pinned entries; version 3 adds
 * `requested` entries: a pinned default base revision plus variant parameters
 * and their deterministic variant identity. The published variant is resolved
 * by that identity; the entry itself never changes when the job completes.
 */
export function createAssetBindings(value) {
  const table = fields(value, ['contract', 'contractVersion', 'entries']);
  check(table.contract === 'ks.asset-bindings' && [1, 2, 3].includes(table.contractVersion), 'unsupported contract');
  check(Array.isArray(table.entries) && table.entries.length <= 20000, 'bounded entries required');
  const descriptors = Object.getOwnPropertyDescriptors(table.entries);
  check(Reflect.ownKeys(descriptors).length === table.entries.length + 1, 'dense JSON entries required');
  const seen = new Set(), entries = [];
  for (let index = 0; index < table.entries.length; index++) {
    const descriptor = descriptors[index];
    check(descriptor?.enumerable && Object.hasOwn(descriptor, 'value'), 'entry accessor or hole');
    const raw = descriptor.value;
    const state = Object.getOwnPropertyDescriptor(raw || {}, 'state')?.value;
    check(state === 'pinned' || state === 'unresolved' || (state === 'requested' && table.contractVersion === 3), 'invalid state');
    const withAppearance = ((state === 'pinned' && table.contractVersion >= 2) || state === 'requested') && Object.hasOwn(raw || {}, 'appearance');
    const own = state === 'pinned' ? ['reference'] : state === 'requested' ? ['base', 'parameters', 'variantId'] : ['reason'];
    const entry = fields(raw, ['objectId', 'componentId', 'articleNumber', 'state', ...own, ...(withAppearance ? ['appearance'] : [])]);
    for (const key of ['objectId', 'componentId', 'articleNumber']) text(entry[key], key);
    const identity = JSON.stringify([entry.objectId, entry.componentId]);
    check(!seen.has(identity), 'duplicate component'); seen.add(identity);
    if (state === 'pinned') {
      try { entry.reference = createAssetReference(entry.reference); }
      catch { check(false, 'invalid geometry reference'); }
      if (withAppearance) entry.appearance = createLegacyAssetAppearance(entry.appearance);
      check(!entry.appearance?.legacySvgViewRegionColors || table.contractVersion === 3, 'view colours require version 3');
    } else if (state === 'requested') {
      try { entry.base = createAssetReference(entry.base); }
      catch { check(false, 'invalid base reference'); }
      check(entry.base.variantId === 'default', 'base must be the default variant');
      try { entry.parameters = createAssetVariantParameters(entry.parameters); }
      catch { check(false, 'invalid variant parameters'); }
      check(typeof entry.variantId === 'string' && entry.variantId === assetVariantId(entry.base.packageDigest, entry.parameters),
        'variant identity does not match base/parameters');
      if (withAppearance) entry.appearance = createLegacyAssetAppearance(entry.appearance);
    } else check(REASONS.has(entry.reason), 'invalid unresolved reason');
    entries.push(Object.freeze(entry));
  }
  return Object.freeze({ contract: table.contract, contractVersion: table.contractVersion, entries: Object.freeze(entries) });
}

/** Read the declared path relative to one owner's data. Missing stays missing. */
export function readSystemAssetBindings(contract, data) {
  if (!contract?.assetBindingsPath) return null;
  let value = data;
  for (const segment of contract.assetBindingsPath) {
    if (value === undefined) return null;
    check(value !== null && typeof value === 'object' && !Array.isArray(value), 'invalid path container');
    const descriptor = Object.getOwnPropertyDescriptor(value, segment);
    if (!descriptor) return null;
    check(Object.hasOwn(descriptor, 'value'), 'path accessor'); value = descriptor.value;
  }
  return createAssetBindings(value);
}
